import type { Dictionary } from "./de";

export const en: Dictionary = {
  common: {
    cancel: "Cancel",
    save: "Save",
    create: "Create",
    loading: "Loading …",
    deleteForever: "Delete permanently",
    optional: "optional",
    error: "Something went wrong.",
  },
  app: {
    updateBanner: "New version available — click to reload",
    navTours: "Tours",
    switchAccount: "Switch account",
    newAccount: "New account …",
    newAccountTitle: "Create new account",
    newAccountPlaceholder: "Company name",
    noAccountTitle: "No account",
    noAccountText:
      "You are not a member of any account. Ask an account admin or the operator to add you.",
    noAccountOperatorText: "You are not in any account yet. Create one in the switcher above.",
  },
  nav: {
    projects: "Projects",
    noProjects: "No projects yet",
    noTours: "No tours",
    tourCount: (n: number) => `${n} tour${n === 1 ? "" : "s"}`,
    expand: "Expand",
    collapse: "Collapse",
    menu: "Navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    breadcrumb: "Breadcrumb",
  },
  menu: {
    navigation: "Navigation",
    design: "Theme",
    language: "Language",
    settings: "Settings",
    signOut: "Sign out",
  },
  settings: {
    title: "Settings",
    navProfile: "Profile",
    navAccountGroup: "Account",
    navGeneral: "General",
    navUsers: "Users",
  },
  theme: {
    light: "Light",
    system: "System",
    dark: "Dark",
  },
  account: {
    title: "Profile",
    nameSection: "Name",
    nameChanged: "Name changed.",
    emailSection: "Email address",
    currentEmail: (email: string) => `Current: ${email}`,
    newEmailPlaceholder: "new@address.com",
    change: "Change",
    emailChanged: "Email address changed.",
    emailTaken: "Change not possible — is the address already in use?",
    passwordSection: "Password",
    currentPassword: "Current password",
    newPassword: (min: number) => `New password (min. ${min} characters)`,
    changePassword: "Change password",
    passwordChanged: "Password changed — other devices were signed out.",
    wrongPassword: "Current password is incorrect.",
  },
  company: {
    title: "Account",
    nameLabel: "Account name",
    nameChanged: "Name changed.",
    memberHint: "Account changes are made by an admin.",
    storageSection: "Storage usage",
    storageHint: "Original images plus all rendered variants and posters.",
    tourCount: (n: number) => `${n} tour${n === 1 ? "" : "s"}`,
    billingHint: "Billing & plans will live here later.",
    loadError: "Could not load account data.",
  },
  twofa: {
    section: "Two-factor authentication",
    statusOn: "Active",
    statusOff: "Inactive",
    enabledHint: "A code is sent to your email when signing in. Enter your password to deactivate.",
    disabledHint:
      "Require an additional email code when signing in. Enter your password to activate.",
    enable: "Activate",
    disable: "Deactivate",
    enabledMsg: "Two-factor authentication is now active.",
    disabledMsg: "Two-factor authentication is deactivated.",
    title: "Verification code",
    prompt: "We sent a sign-in code to your email.",
    sent: "Code sent — please check your inbox.",
    sendFailed: "The code could not be sent.",
    wrongCode: "Code invalid or expired.",
    trustDevice: "Trust this device for 30 days",
    verify: "Sign in",
    resend: "Resend code",
    back: "Back to sign-in",
  },
  login: {
    tagline: "360° tours for real estate",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    errorBanned: "This account has been suspended.",
    errorTooMany: "Too many attempts — please wait a moment.",
    errorOrigin: "Wrong URL — please sign in via the correct address.",
    errorInvalid: "Wrong email or password.",
    forgot: "Forgot password?",
    forgotTitle: "Reset password",
    forgotText: "We'll send a reset link to your email address.",
    forgotSubmit: "Request link",
    forgotSent:
      "If this address exists in our system, a link is on its way — please check your inbox.",
  },
  reset: {
    title: "Set a new password",
    passwordPlaceholder: (min: number) => `New password (min. ${min} characters)`,
    submit: "Save password",
    success: "Password changed — you can sign in now.",
    invalid: "This link is invalid or has expired.",
    requestNew: "Request a new link",
    toLogin: "Go to sign-in",
    failed: "The password could not be set — please request a new link.",
  },
  invite: {
    title: "Invitation",
    intro: (account: string, role: string) => `You've been invited to “${account}” as ${role}.`,
    newUserText: (email: string) =>
      `Create a login for ${email}: choose a name and password, then you're in.`,
    namePlaceholder: "Your name",
    createAndAccept: "Create login and join",
    signInFirst:
      "A login already exists for this email. Sign in, then you can accept the invitation.",
    acceptAs: (email: string) => `Signed in as ${email}.`,
    accept: "Accept invitation",
    mismatch: (current: string, invited: string) =>
      `You are signed in as ${current}, but the invitation is for ${invited}. Sign out and open the link again.`,
    invalid: "This invitation link is invalid or has been withdrawn.",
    expired: "This invitation link has expired — please ask for a new invitation.",
    used: "This invitation has already been accepted.",
  },
  dashboard: {
    title: "Tours",
    newTour: "New tour",
    emptyTitle: "No tours yet",
    emptyText: "Create your first tour and upload 360° images.",
    firstTour: "Create first tour",
    noScenes: "No scenes yet",
    sceneCount: (n: number) => `${n} scene${n === 1 ? "" : "s"}`,
    readySuffix: (n: number) => ` · ${n} ready`,
    statusDraft: "Draft",
    statusPublished: "Published",
    statusPublishedChanges: "Published · pending changes",
    statusLocked: "Password protected",
    createTitle: "New tour",
    tourNameLabel: "Tour name",
    tourNamePlaceholder: "e.g. 1 Main Street, ground floor",
    projectLabel: "Project (property)",
    newProjectOption: "New project (tour name)",
    editProjectTooltip: "Edit project",
  },
  project: {
    title: "Project",
    emptyTitle: "No tours in this property yet",
    editTitle: "Edit project",
    nameLabel: "Name (property)",
    addressLabel: "Address",
    addressPlaceholder: "1 Main Street, Springfield",
    moreActions: "More actions",
    edit: "Edit project",
    delete: "Delete project",
    deleteTitle: (name: string) => `Delete project “${name}”?`,
    deleteText: (n: number) =>
      n === 1
        ? "This property’s tour will be deleted permanently, with all its scenes, images and hotspots."
        : `All ${n} tours of this property will be deleted permanently, with all their scenes, images and hotspots.`,
    deleteTextEmpty: "This property has no tours and will be removed.",
    deletePublished: "Published tours are included — their links will stop working.",
  },
  tour: {
    settings: "Settings",
    tabScenes: "Scenes",
    settingsSaved: "Saved",
    moreActions: "More actions",
    delete: "Delete",
    preview: "Preview",
    previewHint: "View the current draft — signed-in users only",
    settingsTitle: "Tour settings",
    sectionGeneral: "General",
    sectionDisplay: "Display",
    nameLabel: "Name",
    viewLimit: "Hide tripod",
    viewLimitHint: "Limits looking down in the tour — the tripod stays out of sight.",
    viewLimitOff: "Off",
    viewLimitSoft: "Subtle (down to −75°)",
    viewLimitMedium: "Medium (down to −60°)",
    viewLimitStrong: "Strong (down to −45°)",
    gyroButton: "Motion control",
    gyroButtonHint: "Shows a button on phones and tablets for looking around by moving the device.",
    sectionHotspots: "Hotspots",
    hotspotHint: "Appearance of the navigation markers in this tour.",
    hotspotPreview: "Preview",
    hotspotPreviewSample: "Example",
    hotspotVariant: "Style",
    hotspotVariantDot: "Dot",
    hotspotVariantRing: "Ring",
    hotspotVariantArrow: "Arrow",
    hotspotVariantPill: "Label",
    hotspotColor: "Color",
    hotspotSize: "Size",
    hotspotSizeS: "Small",
    hotspotSizeM: "Medium",
    hotspotSizeL: "Large",
    hotspotPulse: "Pulse",
    deleteTitle: (name: string) => `Delete tour “${name}”?`,
    deleteText: "All scenes, images and hotspots of this tour will be deleted permanently.",
    dropTitle: "Drop 360° images here",
    dropText: "or click to choose — equirectangular JPGs (2:1), e.g. Insta360 Studio export",
    dropOverlayTitle: "Drop 360° images",
    orDrop: "or just drop images anywhere on the page",
    addScenes: "Add scenes",
    openScene: "Open in walkthrough",
    dragToSort: "Drag to reorder",
    hotspotCount: (n: number) => `${n} hotspot${n === 1 ? "" : "s"}`,
    hotspotNone: "No hotspots",
    renameScene: "Rename",
    replaceScene: "Replace image",
    deleteScene: "Delete",
    renameSceneTitle: "Rename scene",
    deleteSceneTitle: (name: string) => `Delete scene “${name}”?`,
    deleteSceneText: "The image and all rendered variants will be deleted permanently.",
    deleteScenePublished:
      "The published tour keeps showing this scene until you publish the changes.",
    replaceSceneTitle: (name: string) => `Replace image of “${name}”`,
    replaceSceneText:
      "Hotspots, links, name and start view stay as they are — only the image is swapped. Made for a re-edit of the same shot (exposure, retouching).",
    replaceScenePublished:
      "The published tour keeps showing the old image until you publish the changes.",
    replaceSceneWarn:
      "If the new image is aligned differently, the hotspots will no longer sit where they belong.",
    replaceScenePick: "Choose image",
    restoreImage: "Restore previous image",
    failedBanner: (n: number) =>
      n === 1
        ? "One image could not be added — see the card for details."
        : `${n} images could not be added — see the cards for details.`,
    removeFailed: (n: number) => (n === 1 ? "Remove" : `Remove all ${n}`),
  },
  // Upload/processing display on the tour page (summary bar + card states)
  upload: {
    titleUploading: "Uploading images",
    titleProcessing: "Processing images",
    uploading: (done: number, total: number) => `${done} of ${total} uploaded`,
    processing: (done: number, total: number) => `${done} of ${total} processed`,
    remaining: (text: string) => `about ${text} left`,
    minutes: (n: number) => `${n} min`,
    seconds: (n: number) => `${n} s`,
    failed: (n: number) => `${n} failed`,
    allDone: (n: number) => `${n} ${n === 1 ? "scene is" : "scenes are"} ready`,
    dismiss: "Dismiss",
    leaveTitle: "Uploads still running",
    leaveText: (running: number, done: number) =>
      `${running === 1 ? "One upload is" : `${running} uploads are`} still running. Leaving this page ` +
      "cancels them and removes the affected scenes" +
      (done > 0
        ? ` — the ${done === 1 ? "image already uploaded stays" : `${done} images already uploaded stay`}.`
        : "."),
    leaveStay: "Stay here",
    leaveAnyway: "Leave anyway",
    stateWaiting: "Waiting",
    stateUploading: (pct: number) => `Uploading ${pct} %`,
    stateQueued: (pos: number) => `In queue · position ${pos}`,
    stateProcessing: "Processing",
    stateReady: "Ready",
    phaseAnalyze: "Checking image",
    phaseTiles: "Rendering tiles",
    phaseBases: "Rendering base images",
    phasePoster: "Rendering preview image",
  },
  pipeline: {
    unreadable: "Not a readable JPG/PNG/WebP image.",
    wrongRatio: (ratio: string) =>
      `Not a 360° image: aspect ratio is ${ratio}:1 instead of 2:1 (equirectangular). ` +
      "Please export dual-fisheye files as 360° JPG in Insta360 Studio.",
    tooSmall: (px: string) => `Resolution too low: ${px} px wide (minimum 4096 px).`,
    noOriginal: "No original uploaded.",
    failed: "Processing failed — please try again.",
  },
  password: {
    tooShort: (min: number) => `At least ${min} characters.`,
    tooLong: (max: number) => `At most ${max} characters.`,
    personal: "The password must not contain parts of your email address or name.",
    weak: "Too easy to guess — use a longer phrase or several random words.",
    compromised: "This password has appeared in known data breaches — please choose another one.",
    strength: (score: number) => ["Very weak", "Weak", "Fair", "Good", "Strong"][score] ?? "",
  },
  apiError: {
    unauthorized: "Not signed in — please sign in again.",
    forbidden: "No permission.",
    invalidInput: "Invalid input.",
    tourNotFound: "Tour not found.",
    sceneNotFound: "Scene not found.",
    hotspotNotFound: "Hotspot not found.",
    projectNotFound: "Project not found.",
    accountNotFound: "Account not found.",
    noAccount: "You are not a member of any account.",
    alreadyMember: "This person is already a member.",
    invalidRole: "Invalid role.",
    invitationInvalid: "This invitation link is invalid.",
    invitationExpired: "This invitation link has expired.",
    emailMismatch: "The invitation is for a different email address.",
    userExists: "A login already exists for this email — please sign in.",
    invalidTargetScene: "Invalid target scene.",
    unsupportedMediaType: "Only JPG/PNG/WebP — please export as 360° JPG in Insta360 Studio.",
    sceneProcessing: "Scene is currently being processed.",
    fileTooLarge: "File too large (max. 200 MB).",
    emptyUpload: "Empty upload.",
    uploadFailed: "Upload failed — please try again.",
    noReadyScene: "No finished scene — there is nothing to publish.",
    noPreviousImage: "The previous image is no longer available.",
    publishedSceneNotReady:
      "A scene that is already published is not ready right now — wait for processing to finish or restore its previous image.",
    sceneListMismatch: "Scene list does not match the tour.",
    network: "Network error — please check your connection.",
    http: (status: number) => `Error (${status})`,
  },
  share: {
    publish: "Publish",
    republish: "Publish changes",
    unpublish: "Unpublish",
    share: "Share",
    title: "Publish & share",
    draftState: "Not published yet — the link only works once you publish.",
    draftNoScene: "You can publish as soon as the first scene is ready.",
    publishedState: (date: string) => `Published version: ${date}`,
    upToDate: "Up to date",
    pendingChanges: "Pending changes",
    republishHint: "Editor changes only go public via “Publish changes”.",
    blockedHint:
      "A published scene is not ready right now — until it is, publishing would drop it from the tour. Wait for processing to finish, or restore the previous image on its card.",
    linksAfterPublish: "The public link and embed code appear here once the tour is published.",
    showPassword: "Show password",
    hidePassword: "Hide password",
    publicLink: "Public link",
    embedCode: "iframe embed code",
    copy: "Copy",
    copied: "Copied ✓",
    openTour: "Open tour",
    password: "Password protection",
    passwordHint:
      "Optional — visitors must enter the password. Takes effect immediately, no publishing needed.",
    passwordSet: "A password is set — overwrite it to change.",
    passwordPlaceholder: "Password (min. 4 characters)",
    passwordSave: "Set",
    passwordRemove: "Remove",
    unpublishTitle: "Unpublish this tour?",
    unpublishText: "The public link and all embeds will show “not found” until you publish again.",
  },
  stats: {
    title: "Statistics",
    viewsPerDay: "Views and visitors per day",
    range30: "30 days",
    range90: "90 days",
    rangeAll: "All time",
    sources: "Sources",
    devices: "Devices",
    direct: "Direct / no source",
    mobile: "Mobile",
    desktop: "Desktop",
    empty: "No views in this period yet.",
    loadError: "Could not load statistics.",
    views: "Views",
    visitors: "Visitors",
    overview: (days: number) => `Last ${days} days · all tours`,
    visitorsHintAll: "unique per day and tour",
    visitorsHint: "unique per day, no cookies",
    avgDuration: "Avg. time viewed",
    avgDurationHint: "visible time only",
    bounceRate: "Bounce rate",
    bounceHint: "under 10 s, no interaction",
    interactionRate: "Interaction rate",
    interactionHint: "rotated, changed scene, hotspot",
    hotspotClicks: "Hotspot clicks",
    fullscreen: "Used fullscreen",
    trend: "Trend",
    duration: "Time viewed",
    durationHint:
      "Counts only what is actually being watched: tab active, tour in view, visitor not idle for more than a minute.",
    buckets: ["under 10 s", "10–30 s", "30–60 s", "1–3 min", "over 3 min"],
    sessionsCaption: (n: number) => `${n} completed session${n === 1 ? "" : "s"}`,
    pendingHint: "Sessions still running are added once they end, at the latest after 10 minutes.",
    scenes: "Scenes",
    sceneCol: "Scene",
    sceneViews: "Views",
    sceneReach: "Share of sessions",
    sceneAvg: "Avg. time",
    sceneEntries: "Entry",
    sceneExits: "Exit",
    scenesEmpty: "No scene data in this period yet.",
    languages: "Languages",
    engagement: "Engagement",
    seconds: (s: number) => `${s} s`,
    minutes: (m: number, s: number) => `${m}:${String(s).padStart(2, "0")} min`,
  },
  editor: {
    editTour: "Edit walkthrough",
    smallScreenTitle: "Not enough room",
    smallScreenText:
      "The walkthrough editor needs a larger screen — hotspots are placed by dragging inside the panorama. Open it on a tablet (landscape) or a computer.",
    back: "Back to tour",
    close: "Close",
    settings: "Settings",
    mobilePreview: "Mobile preview",
    mobilePreviewOff: "Back to full view",
    scenes: "Scenes",
    hotspots: "Hotspots in this scene",
    noHotspots: "No hotspots yet — click into the panorama to add one.",
    clickHint: "Click into the image to place a hotspot",
    noReadyScenes: "No processed scenes yet — upload 360° images first.",
    placeTitle: "Navigation hotspot",
    hotspotDrawerTitle: "Edit hotspot",
    placeTarget: "Target scene",
    placeLabel: "Label",
    placeLabelHint: "empty = target scene name",
    target: "Target",
    arrival: "Arrival view",
    arrivalAuto: "Automatic (target scene's start view)",
    arrivalManual: "Set manually",
    arrivalStart: "Set view …",
    arrivalBanner: "Adjust the view in the target scene, then save.",
    arrivalSave: "Save as arrival view",
    arrivalReset: "Reset to automatic",
    reposition: "Reposition",
    repositionBanner: "Click the new position in the image.",
    deleteHotspot: "Delete hotspot",
    startView: "Save as start view",
    startViewSaved: "Start view saved",
    startViewDirty: "This view is not saved as start — click to save",
    startViewClean: "Matches the saved start view",
    startViewReset: "Jump back to the saved start view",
  },
  members: {
    title: "Users",
    add: "Add user",
    addTitle: "Add person",
    inviteHint:
      "Existing logins become members right away. New people receive an email " +
      "invitation (valid 7 days) and set their own password.",
    invited: "Invitation sent.",
    added: "Person added.",
    pendingTitle: "Pending invitations",
    pendingHint:
      "Not accepted yet. Resending creates a new link with a fresh deadline; " +
      "you can also copy the link and send it yourself.",
    copyLink: "Copy invitation link",
    linkCopied: "Link copied.",
    validUntil: "Valid until",
    resend: "Resend",
    resent: "Invitation sent again.",
    cancelInvite: "Withdraw",
    canceled: "Invitation withdrawn.",
    name: "Name",
    email: "Email",
    role: "Role",
    roleOwner: "Admin",
    roleMember: "Member",
    you: "(you)",
    tooltipRemove: "Remove from account",
    lastOwnerHint: "Last admin — at least one must remain.",
    removeTitle: (name: string) => `Remove “${name}” from this account?`,
    removeText: "The person loses access to this account. Their login and other accounts are kept.",
    removeSelfText:
      "You lose access to this account and will land in your next account afterwards.",
    remove: "Remove",
    listError: "Could not load the member list.",
    actionError: "Action failed.",
  },
};
